import { Order } from "./shop/order/order";
import { Content } from "../../infrastructure/presentation/views/content/helpers";
import { EnrolledTrainingTypes } from "../enums/course";
import { Menu } from "./courseEvent/training_course";

export class User {
  public login: string;
  public password: string;
  public isFirstLogin: boolean;
  public attendance: boolean;
  public attendanceAts: AttendanceAt[];
  public passwordConfirmation: string;
  public id: number;
  public name: string;
  public socialName: string;
  public profession: string;
  public academicEducation: string;
  public biography: string;
  public document: string;
  public email: string;
  public birthDate: string;
  public image: string;
  public profilePictureExtension: string;
  public address: Address;
  public dcoins: number;
  public completeCourses: number;
  public totalWatched: number;
  public userType: number;
  /** Only used when the user comes from an EventClass
   * @see EventClass */
  public enrolledAt: string = "";
  public attendanceAt: string = "";
  public isImported: boolean = false;
  public passwordModifiedAt: string = "";
  public certificateGenerated: string;
  public percentageUser: number;
  public interests: Interest[];
  public isForeigner: boolean;
  public needAcceptNewTerm: boolean;
  public companies: Company[];

  constructor(
    login = "",
    password = "",
    passwordConfirmation = "",
    id = NaN,
    name = "",
    socialName = "",
    profession = "",
    academicEducation = "",
    biography = "",
    firstLogin = true,
    document = "",
    email = "",
    birthDate = "",
    image = "",
    profilePictureExtension = "",
    address: Address = new Address(),
    dcoins: number = 0,
    completeCourses: number = 0,
    totalWatched: number = 0,
    userType: number = 0,
    isImported = false,
    passwordModifiedAt = "",
    certificateGenerated = "",
    percentageUser = NaN,
    interests = new Array<Interest>(),
    isForeigner = false,
    attendance = false,
    needAcceptNewTerm = false,
    attendanceAts = new Array<AttendanceAt>(),
    companies = new Array<Company>()
  ) {
    this.id = id;
    this.login = login;
    this.password = password;
    this.isFirstLogin = firstLogin;
    this.passwordConfirmation = passwordConfirmation;
    this.name = name;
    this.socialName = socialName;
    this.profession = profession;
    this.biography = biography;
    this.academicEducation = academicEducation;
    this.document = document;
    this.email = email;
    this.birthDate = birthDate;
    this.image = image;
    this.profilePictureExtension = profilePictureExtension;
    this.address = address;
    this.dcoins = dcoins;
    this.completeCourses = completeCourses;
    this.totalWatched = totalWatched;
    this.userType = userType;
    this.isImported = isImported;
    this.passwordModifiedAt = passwordModifiedAt;
    this.certificateGenerated = certificateGenerated;
    this.percentageUser = percentageUser;
    this.interests = interests;
    this.isForeigner = isForeigner;
    this.attendance = attendance;
    this.needAcceptNewTerm = needAcceptNewTerm;
    this.attendanceAts = attendanceAts;
    this.companies = companies;
  }
}

export class Address {
  public addressNumber: number;
  public street: string;
  public district: string;
  public federalUnit: string;
  public stateID: number;
  public city: string;
  public zipCode: string;
  public complement: string;

  constructor(
    addressNumber = NaN,
    street = "",
    district = "",
    federalUnit = "",
    city = "",
    zipCode = "",
    stateID = NaN,
    complement = ""
  ) {
    this.addressNumber = addressNumber;
    this.street = street;
    this.district = district;
    this.federalUnit = federalUnit;
    this.stateID = stateID;
    this.city = city;
    this.zipCode = zipCode;
    this.complement = complement;
  }
}

/** Represents a course the user is currently doing or have already finished. */
export class UserCourse {
  public enrolled: boolean;
  public finished: boolean;

  constructor(enrolled = false, finished = false) {
    this.enrolled = enrolled;
    this.finished = finished;
  }
}

/** The school entity's purpose is to serve as a top level category that hold course tracks with topics in common. */
export class School {
  public id: number;
  public description: string;
  public name: string;
  public thumbnailURL: string;
  public thumbnailExtention: string;
  public isActive: boolean;
  public lastChange: string;
  public courseTracks: Array<CourseTrack>;

  constructor(
    id = NaN,
    description = "",
    name = "",
    thumbnailURL = "",
    thumbnailExtention = "",
    isActive = true,
    lastChange = "",
    courseTracks = new Array<CourseTrack>()
  ) {
    this.id = id;
    this.description = description;
    this.name = name;
    this.thumbnailURL = thumbnailURL;
    this.thumbnailExtention = thumbnailExtention;
    this.isActive = isActive;
    this.lastChange = lastChange;
    this.courseTracks = courseTracks;
  }
}

/** The course track entity is a sequence of courses that share the same goal within the student's learning. */
export class CourseTrack {
  public schoolId: number;
  public id: number;
  public title: string;
  public description: string;
  public isActive: boolean;
  public lastChange: string;
  public thumbnailURL: string;
  public thumbnailExtention: string;

  constructor(
    schoolId = NaN,
    id = NaN,
    title = "",
    description = "",
    isActive = true,
    lastChange = "",
    thumbnailURL = "",
    thumbnailExtention = ""
  ) {
    this.schoolId = schoolId;
    this.id = id;
    this.title = title;
    this.description = description;
    this.isActive = isActive;
    this.lastChange = lastChange;
    this.thumbnailURL = thumbnailURL;
    this.thumbnailExtention = thumbnailExtention;
  }
}

export class ContentComment {
  public userName;
  public userImage;
  public message;
  public courseName;
  public contentName;
  public rating;
  public createAt;
}

export enum StatusCode {
  DELETED = 2
}

export class StatusCourse {
  public finished: boolean;
  public progress: boolean;

  constructor(progress = false, finished = false) {
    this.progress = progress;
    this.finished = finished;
  }
}
export class Course {
  public id: number;
  public idSubcategory: number;
  public title: string;
  public description: string;
  public duration: number;
  public rating: string;
  public isNew: boolean;
  public isPill: boolean;
  public isWatched: boolean;
  public haveCertification: boolean;
  public imageURL: string;
  public bannerURL: string;
  public imageExtension: string;
  public bannerExtension: string;
  public isActive: boolean;
  public activationDate: string;
  public inactivationDate: string;
  public lastChange: string;
  public formattedDuration: string;
  public certificatePath: string;
  public courseProgress: number;
  public hasNewContent: boolean;
  public statusCode: number;
  public haveComments: boolean;
  public introductoryContent: Content | null;
  public releaseStartDate: string;
  public releaseEndDate: string;
  public statusCourse: StatusCourse;
  public haveDecoins: boolean;
  public contents: Content[];
  public isFavorite: boolean;

  constructor(
    idSubcategory = NaN,
    id = NaN,
    title = "",
    description = "",
    duration = NaN,
    rating = "",
    isNew = false,
    isPill = false,
    isWatched = false,
    haveCertification = false,
    imageURL = "",
    bannerURL = "",
    imageExtension = "",
    bannerExtension = "",
    isActive = true,
    activationDate = "",
    inactivationDate = "",
    lastChange = "",
    formattedDuration = "",
    certificatePath = "",
    courseProgress = NaN,
    hasNewContent = false,
    statusCode = NaN,
    haveComments = false,
    introductoryContent = null,
    releaseStartDate = "",
    releaseEndDate = "",
    statusCourse = new StatusCourse(),
    haveDecoins = false,
    contents = [],
    isFavorite = false,
  ) {
    this.idSubcategory = idSubcategory;
    this.id = id;
    this.title = title;
    this.description = description;
    this.duration = duration;
    this.rating = rating;
    this.isNew = isNew;
    this.isPill = isPill;
    this.isWatched = isWatched;
    this.haveCertification = haveCertification;
    this.imageURL = imageURL;
    this.bannerURL = bannerURL;
    this.imageExtension = imageExtension;
    this.bannerExtension = bannerExtension;
    this.isActive = isActive;
    this.activationDate = activationDate;
    this.inactivationDate = inactivationDate;
    this.lastChange = lastChange;
    this.formattedDuration = formattedDuration;
    this.certificatePath = certificatePath;
    this.courseProgress = courseProgress;
    this.hasNewContent = hasNewContent;
    this.statusCode = statusCode;
    this.haveComments = haveComments;
    this.introductoryContent = introductoryContent;
    this.releaseStartDate = releaseStartDate;
    this.releaseEndDate = releaseEndDate;
    this.statusCourse = statusCourse;
    this.haveDecoins = haveDecoins;
    this.contents = contents;
    this.isFavorite = isFavorite;
  }
}

export class FilterCourse {
  public schools: number[];
  public courseTracks: number[];

  constructor(schools = [], courseTracks = []) {
    this.schools = schools;
    this.courseTracks = courseTracks;
  }
}

export type Instructor = {
  id: number;
  name: string;
  biography: string;
  academicEducation: string;
  image: string;
};

export class Annotation {
  public content: string;
  public seconds: number;
  public id: number;

  constructor(content = "", seconds = NaN, id = NaN) {
    this.content = content;
    this.seconds = seconds;
    this.id = id;
  }
}

export class UserRating {
  public userName: string;
  public userImage: string;
  public stars: number;
  public message: string;
  public createdAt: string;

  constructor(
    userName = "",
    userImage = "",
    stars = 0,
    message = "",
    createdAt = ""
  ) {
    this.userImage = userImage;
    this.userName = userName;
    this.stars = stars;
    this.message = message;
    this.createdAt = createdAt;
  }
}

export class Interest {
  public id: number;
  public isActive: boolean;
  public title: string;

  constructor(id = 0, isActive = true, title = "") {
    this.id = id;
    this.isActive = isActive;
    this.title = title;
  }
}

/** Used when the user is trying to recover their password and do not have a Decola account */
export class UserData {
  public name: string;
  public birthDate: string;
  public badgeCode: number;
  public document: string;
  public mothersName: string;
  public email: string;

  constructor(
    name = "",
    birtDate = "",
    badgeCode = NaN,
    document = "",
    mothersName = "",
    email = ""
  ) {
    this.name = name;
    this.birthDate = birtDate;
    this.badgeCode = badgeCode;
    this.document = document;
    this.mothersName = mothersName;
    this.email = email;
  }
}

/** Used to check password strength. */
export class PasswordStrength {
  /** Password strength value. */
  public entropy: number;

  /** Minimum strength required for the password to be considered acceptable. Default value 60. */
  public minEntropy: number;

  constructor(entropy = NaN, minEntropy = NaN) {
    this.entropy = entropy;
    this.minEntropy = minEntropy;
  }
}

/** CourseCertificate used to redirect user to the certificate path
 * that is generated when user is getting the certificate by request.
 * @path is the http location where the certificate is stored. */
export class CourseCertificate {
  path: string;

  constructor(path = "") {
    this.path = path;
  }
}

export type UserTeam = {
  id: number;
  name: string;
  managerName: string;
  totalOfMembers: number;
};

export type Company = {
  id: number;
  name: string;
};

export type UserRanking = {
  id: number;
  name: string;
  image: string;
  companies: Company[];
  contentDuration: number;
};

export type UserGeneralStatistics = {
  average: number;
  releasedCourses: number;
  coursesInProgress: number;
  coursesFinished: number;
  hoursWatched: number;
  quantityAccessed: number;
};

export type UserAccess = {
  period: string;
  quantity: number;
};

export class EnrolledTraining {
  public id: number;
  public name: string;
  public description: string;
  public duration: number;
  public rating: string;
  public haveCertification: boolean;
  public hasNewContent: boolean;
  public imageURL: string;
  public bannerURL: string;
  public imageExtension: string;
  public bannerExtension: string;
  public isActive: boolean;
  public activationDate: string;
  public inactivationDate: string;
  public lastChange: string;
  public certificatePath: string;
  public progress: number;
  public percentage: number;
  public statusCode: number;
  public type: EnrolledTrainingTypes;
  public menus: Menu[];

  constructor(
    id = 0,
    name = "",
    description = "",
    duration = 0,
    rating = "",
    haveCertification = false,
    hasNewContent = false,
    certificatePath = "",
    imageURL = "",
    imageExtension = "",
    bannerURL = "",
    bannerExtension = "",
    isActive = false,
    activationDate = "",
    inactivationDate = "",
    lastChange = "",
    progress = 0,
    percentage = 0,
    statusCode = 0,
    type = EnrolledTrainingTypes.ONLINE,
    menus = new Array<Menu>()
  ) {
    this.id = id;
    this.name = name;
    this.description = description;
    this.duration = duration;
    this.rating = rating;
    this.haveCertification = haveCertification;
    this.hasNewContent = hasNewContent;
    this.certificatePath = certificatePath;
    this.imageURL = imageURL;
    this.imageExtension = imageExtension;
    this.bannerURL = bannerURL;
    this.bannerExtension = bannerExtension;
    this.isActive = isActive;
    this.activationDate = activationDate;
    this.inactivationDate = inactivationDate;
    this.lastChange = lastChange;
    this.progress = progress;
    this.percentage = percentage;
    this.statusCode = statusCode;
    this.type = type;
    this.menus = menus;
  }
}

export class AttendanceAt {
  public attendanceAt: string;
  public trainingDate: string;
  public id: number;

  constructor(attendanceAt = "", trainingDate = "", id = 0) {
    this.attendanceAt = attendanceAt;
    this.trainingDate = trainingDate;
    this.id = id;
  }
}
